/**
 * 행사(이벤트) 일정·무대 — FR-EVENT, 기획서 §2.5 (M3).
 * 행사 시간에만 무대 화상 오픈(FR-EVENT-01). 정원 초과 시 방송형(YouTube) 전환(FR-EVENT-03).
 * 콘텐츠 주도: content/events.yaml → events.json.
 */

import { configuredUrl } from "./placeholder.ts"; // 🔴 확장자 — scripts/check-owner.mjs 가 node 로 직접 읽는다

export interface EventStage {
  /**
   * 🎥 **화상 회의 주소** — 운영자가 붙여넣는다(웨일온·줌·구글밋·Jitsi 무엇이든).
   *
   * 🔴 예전엔 Jitsi 룸 이름만 받아 `meet.jit.si` 를 **iframe 으로 끼워** 넣었다. 그런데
   * 8x8 은 끼워 쓰는 것을 데모로만 허용해서 **5분 뒤 통화가 끊긴다**(2026-09-02 운영자가
   * 행사 중에 그 안내를 받았다). 수업 한 시간도, 북토크도 그걸로는 못 한다.
   *
   * 그래서 **어느 서비스냐를 코드에 안 박는다.** 주소만 받아 새 창으로 연다 —
   * 서비스를 바꿔도 코드가 안 바뀌고, 끼워 넣기의 제약(시간·권한)도 통째로 사라진다.
   */
  meetUrl?: string;
  jitsi?: string; // (옛) Jitsi 룸 이름 — meetUrl 이 없을 때만 쓴다
  youtubeLive?: string; // 방송형 대안(정원 초과/폴백)
  capacity: number; // 화상 동시 인원 상한(초과 시 방송형)
}

export interface EventInfo {
  title: string;
  start?: string; // ISO8601
  end?: string; // ISO8601
  applyUrl?: string; // 외부 폼(보호자 작성)
  stage?: EventStage;
  /**
   * 아이 화면에 보일까. 관리자 [🎁 행사·랠리] 의 스위치가 이 값이다.
   *
   * ## 왜 필요한가 (2026-08-20 실제 사고)
   * 운영에 **「체험 무대 (데모)」가 "지금 열리고 있어요" 로 떠 있었다.** 저장소의 예시 일정이
   * 그대로 아이 화면까지 나간 것이다. `REPLACE` 주소는 이미 걸러지는데 방 이름
   * (`greg-chick-demo-stage`)이 남아서 무대가 열린 것으로 잡혔다.
   *
   * 이름에 "데모"가 들어가면 숨기는 식으로는 못 막는다 — 진짜 「데모 행사」를 열 수도 있고,
   * 반대로 다른 이름의 예시가 또 새어 나간다. **운영자가 켜고 끄는 게 맞다.**
   * 준비 중인 행사를 미리 적어 두고 때가 되면 켜는 쓰임도 같이 생긴다.
   *
   * 값이 아예 없으면 **보이는 것으로 본다** — 이미 게시해 둔 행사가 이 변경 때문에
   * 갑자기 사라지면 그게 더 나쁜 사고다.
   */
  visible: boolean;
}

export type StageMode = "jitsi" | "youtube";

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

export function normalizeEvents(input: unknown): EventInfo[] {
  const arr = Array.isArray(input)
    ? input
    : Array.isArray((input as { events?: unknown })?.events)
      ? (input as { events: unknown[] }).events
      : [];
  return arr
    .map((e): EventInfo | null => {
      if (!e || typeof e !== "object") return null;
      const o = e as Record<string, unknown>;
      const title = str(o.title);
      if (!title) return null;
      const s = o.stage as Record<string, unknown> | undefined;
      const stage: EventStage | undefined =
        s && typeof s === "object"
          ? {
              // 자리표시자(REPLACE·로컬주소)면 없는 것으로 — 없는 방을 열어주지 않는다
              meetUrl: configuredUrl(str(s.meet_url) ?? str(s.meetUrl), false),
              jitsi: str(s.jitsi),
              // 자리표시자(REPLACE)면 없는 것으로 — 없는 방송을 아이에게 열어주지 않는다
              youtubeLive: configuredUrl(str(s.youtube_live) ?? str(s.youtubeLive), false),
              capacity:
                typeof s.capacity === "number" && s.capacity > 0
                  ? Math.floor(s.capacity)
                  : 100,
            }
          : undefined;
      return {
        title,
        start: str(o.start) ?? str(o.date),
        end: str(o.end),
        applyUrl: configuredUrl(str(o.apply_url) ?? str(o.applyUrl), false),
        stage,
        // 안 적혀 있으면 보이는 것 — 이미 게시한 행사가 조용히 사라지지 않게
        visible: o.visible !== false && o.hidden !== true,
      };
    })
    .filter((e): e is EventInfo => e !== null);
}

/**
 * 아이 화면에 내보낼 것만 고른다.
 *
 * 화면마다 `filter(e => e.visible)` 을 다시 쓰면 **한 군데를 빠뜨린다** — 랜딩 배너는
 * 숨겼는데 마을 이야기엔 그대로 뜨는 식이다. 그래서 거르는 자리를 여기 하나로 둔다.
 */
export function visibleEvents(events: EventInfo[]): EventInfo[] {
  return events.filter((e) => e.visible);
}

const ms = (iso?: string): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/** 지금 진행 중인 무대 행사(start≤now≤end, stage 존재). 없으면 null. FR-EVENT-01. */
export function activeEvent(events: EventInfo[], nowMs: number): EventInfo | null {
  for (const e of events) {
    if (!e.stage) continue;
    const s = ms(e.start);
    const en = ms(e.end);
    if (s !== null && en !== null && nowMs >= s && nowMs <= en) return e;
  }
  return null;
}

/** 다가오는 가장 가까운 행사(배너용). 없으면 null. */
export function upcomingEvent(events: EventInfo[], nowMs: number): EventInfo | null {
  let best: EventInfo | null = null;
  let bestT = Infinity;
  for (const e of events) {
    const s = ms(e.start);
    if (s !== null && s > nowMs && s < bestT) {
      best = e;
      bestT = s;
    }
  }
  return best;
}

/** 정원 초과 시 방송형(YouTube)으로 전환(FR-EVENT-03). */
export function pickStageMode(participants: number, capacity: number): StageMode {
  return participants >= capacity ? "youtube" : "jitsi";
}

/**
 * 행사를 **보여줄 순서로** 나눈다 — 지금 열림 → 다가옴(가까운 순) → 지남(최근 순).
 *
 * ## 왜 필요한가
 * 지금까지 화면에는 **한 건만** 보였다(진행 중이거나 가장 가까운 것). 그러면 방문자에게는
 * "가끔 뭔가 하는 곳" 으로 보이고, **출판사에게는 프로그램이 있는 곳으로 안 보인다** —
 * 대관을 팔려면 "이런 행사들이 열립니다" 를 보여줄 수 있어야 한다.
 *
 * 지난 행사도 남긴다. 처음 온 사람에게 **지난 기록이 곧 신뢰**다.
 */
export interface EventBuckets {
  live: EventInfo[];
  upcoming: EventInfo[];
  past: EventInfo[];
}

export function bucketEvents(events: EventInfo[], nowMs: number, pastLimit = 5): EventBuckets {
  const live: EventInfo[] = [];
  const upcoming: EventInfo[] = [];
  const past: EventInfo[] = [];
  for (const e of events) {
    const s = ms(e.start);
    const en = ms(e.end);
    if (s === null) continue; // 시작 시각이 없으면 일정이 아니다
    if (en !== null && nowMs > en) past.push(e);
    else if (nowMs >= s && (en === null || nowMs <= en)) live.push(e);
    else if (s > nowMs) upcoming.push(e);
  }
  upcoming.sort((a, b) => (ms(a.start) ?? 0) - (ms(b.start) ?? 0)); // 가까운 것부터
  past.sort((a, b) => (ms(b.start) ?? 0) - (ms(a.start) ?? 0)); // 최근 것부터
  return { live, upcoming, past: past.slice(0, pastLimit) };
}
